import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [discountTimeLeft, setDiscountTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const packages = [
    {
      id: 'basic',
      name: 'Základný balíček',
      description: 'Ideálne pre menšie balkóny s osnovnými požiadavkami',
      price: 45,
      originalPrice: 55,
      features: [
        'Jednoduché bezpečnostné sklo',
        'Štandardné hliníkové profily',
        'Základná inštalácia',
        'Záruka 5 rokov',
        'Bezplatné zameranie',
        'Základné tesnenie'
      ],
      notIncluded: [
        'Tepelná izolácia',
        'Zvuková izolácia',
        'UV ochrana'
      ],
      popular: false,
      color: 'border-border',
      buttonVariant: 'outline'
    },
    {
      id: 'standard',
      name: 'Štandardný balíček',
      description: 'Najobľúbenejšie riešenie s optimálnym pomerom ceny a kvality',
      price: 65,
      originalPrice: 80,
      features: [
        'Dvojsklo s tepelnou izoláciou',
        'Kvalitné hliníkové profily',
        'Profesionálna inštalácia',
        'Záruka 8 rokov',
        'Bezplatné zameranie a konzultácia',
        'Kvalitné tesnenie a izolačné pásky',
        'UV ochrana',
        'Základná zvuková izolácia'
      ],
      notIncluded: [
        'Prémiové sklo',
        'Integrované žalúzie'
      ],
      popular: true,
      color: 'border-accent ring-2 ring-accent',
      buttonVariant: 'default'
    },
    {
      id: 'premium',
      name: 'Prémiový balíček',
      description: 'Najvyššia kvalita pre náročných zákazníkov',
      price: 85,
      originalPrice: 105,
      features: [
        'Trojsklo s maximálnou izoláciou',
        'Prémiové hliníkové systémy',
        'VIP inštalačný servis',
        'Záruka 10 rokov',
        'Bezplatné zameranie a 3D vizualizácia',
        'Prémiové tesnenie a izolácia',
        'Maximálna UV ochrana',
        'Výborná zvuková izolácia',
        'Možnosť integrovaných žalúzií',
        'Špeciálne bezpečnostné sklo'
      ],
      notIncluded: [],
      popular: false,
      color: 'border-primary',
      buttonVariant: 'outline'
    }
  ];

  const additionalServices = [
    { name: 'Integrované žalúzie', price: '150€', description: 'Automatické žalúzie medzi sklami' },
    { name: 'Farebné profily', price: '50€', description: 'Špeciálne farby hliníkových profilov' },
    { name: 'Expresná inštalácia', price: '100€', description: 'Inštalácia do 24 hodín' },
    { name: 'Rozšírená záruka', price: '80€', description: 'Predĺženie záruky na 15 rokov' }
  ];

  // Countdown timer for monthly discount
  useEffect(() => {
    const targetDate = new Date();
    targetDate?.setMonth(targetDate?.getMonth() + 1);
    targetDate?.setDate(1);
    targetDate?.setHours(0, 0, 0, 0);

    const updateCountdown = () => {
      const now = new Date()?.getTime();
      const distance = targetDate?.getTime() - now;

      if (distance > 0) {
        setDiscountTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleQuoteClick = (packageId) => {
    const element = document.querySelector('#quote-form');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const calculateSamplePrice = (pricePerM2, area = 10) => {
    return Math.round(pricePerM2 * area);
  };

  return (
    <section id="pricing" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="Calculator" size={20} />
            <span className="font-semibold">Transparentné ceny</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-6">
            Cenník zasklenia
          </h2>
          <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto leading-relaxed">
            Žiadne skryté poplatky. Všetky ceny sú konečné a zahŕňajú materiál, prácu a dopravu. 
            Využite aktuálnu mesačnú zľavu.
          </p>
        </div>

        {/* Monthly Discount Banner */}
        <div className="bg-gradient-to-r from-accent to-primary rounded-2xl p-6 mb-12 text-white text-center">
          <div className="flex items-center justify-center space-x-2 mb-4 text-white">
            <Icon name="Percent" size={24} />
            <h3 className="text-2xl font-heading font-bold text-white">
              Mesačná zľava 20% - Iba do konca októbra!
            </h3>
          </div>
          
          {/* Countdown Timer */}
          <div className="flex justify-center space-x-6 mb-4 text-white">
            {Object.entries(discountTimeLeft)?.map(([unit, value]) => (
              <div key={unit} className="text-center">
                <div className="bg-white/20 rounded-lg p-3 min-w-[60px]">
                  <div className="text-2xl font-bold text-white">{value?.toString()?.padStart(2, '0')}</div>
                </div>
                <div className="text-sm mt-1 capitalize text-white">{unit}</div>
              </div>
            ))}
          </div>
          
          <p className="text-white/90">
            Ušetrite až 400€ na zasklení vašeho balkóna. Ponuka platí pre nové objednávky.
          </p>
        </div>

        {/* Pricing Packages */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {packages?.map((pkg) => (
            <div
              key={pkg?.id}
              className={`relative bg-white rounded-2xl shadow-lg border-2 ${pkg?.color} overflow-hidden transition-all duration-500 hover:shadow-xl ${
                selectedPackage === pkg?.id ? 'scale-105' : ''
              }`}
              onClick={() => setSelectedPackage(pkg?.id)}
            >

              <div className="p-8">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-heading font-bold text-primary mb-2">
                    {pkg?.name}
                  </h3>
                  <p className="text-text-secondary font-body mb-6">
                    {pkg?.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-center justify-center space-x-2 mb-2">
                      <span className="text-4xl font-heading font-bold text-primary">
                        {pkg?.price}€
                      </span>
                      <span className="text-text-secondary">/m²</span>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <span className="text-lg text-text-secondary line-through">
                        {pkg?.originalPrice}€/m²
                      </span>
                      <span className="bg-success text-white px-2 py-1 rounded text-sm font-semibold">
                        -20%
                      </span>
                    </div>
                  </div>

                  {/* Sample Calculation */}
                  <div className="bg-muted rounded-lg p-4 mb-6">
                    <div className="text-sm text-text-secondary mb-1">Príklad pre 10m²:</div>
                    <div className="text-2xl font-bold text-accent">
                      {calculateSamplePrice(pkg?.price)}€
                    </div>
                    <div className="text-xs text-text-secondary">
                      Pôvodne: {calculateSamplePrice(pkg?.originalPrice)}€
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-4 mb-8">
                  <h4 className="font-semibold text-primary">Obsahuje:</h4>
                  <ul className="space-y-3">
                    {pkg?.features?.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon name="Check" size={16} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                        <span className="text-sm text-text-secondary font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {pkg?.notIncluded?.length > 0 && (
                    <>
                      <h4 className="font-semibold text-primary mt-6">Neobsahuje:</h4>
                      <ul className="space-y-2">
                        {pkg?.notIncluded?.map((feature, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <Icon name="X" size={16} color="var(--color-destructive)" className="mt-1 flex-shrink-0" />
                            <span className="text-sm text-text-secondary font-body">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  variant={pkg?.buttonVariant}
                  fullWidth
                  size="lg"
                  onClick={() => handleQuoteClick(pkg?.id)}
                  iconName="Calculator"
                  iconPosition="left"
                  className={pkg?.popular ? 'bg-accent hover:bg-accent/90 text-white' : ''}
                >
                  Získať ponuku
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Services */}
        <div className="bg-white rounded-2xl shadow-lg border border-border p-8">
          <h3 className="text-2xl font-heading font-semibold text-primary mb-6 text-center">
            Doplnkové služby
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {additionalServices?.map((service, index) => (
              <div key={index} className="text-center p-4 border border-border rounded-lg hover:border-accent transition-smooth">
                <div className="text-lg font-semibold text-primary mb-2">
                  {service?.name}
                </div>
                <div className="text-2xl font-bold text-accent mb-2">
                  {service?.price}
                </div>
                <div className="text-sm text-text-secondary">
                  {service?.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Price Guarantee */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-4 bg-success/10 text-success px-6 py-4 rounded-full">
            <Icon name="Shield" size={24} />
            <div>
              <div className="font-semibold">Záruka najlepšej ceny</div>
              <div className="text-sm">Nájdete lacnejšie? Vyrovnáme cenu + 5% zľava</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;